import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex flex-col gap-8 md:gap-6"
}
const _hoisted_2 = { class: "flex flex-col gap-8 md:gap-6" }
const _hoisted_3 = { class: "md:w-[413px]" }
const _hoisted_4 = {
  key: 3,
  class: "border-b border-solid border-racq-pale-blue-border my-8 md:my-6"
}

import { onMounted, ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { FormKit, reset } from '@formkit/vue';
import { IDropdownItem, IRadioButtonCard, ISuburbLookupResult } from '@/interfaces';
import { INameValue } from '@/interfaces/entities/name-value.interface';
import { useMultiInstanceAddressOptionsStore, useFormStore, useJeopardyStore } from '@/store';
import { isPostalAddress } from '@/utils/regex-helpers';
import { joinAddressComponents } from '@/mappers';
import { addressLookupEmits, emitManualAddressSelected, useAddressLookupStore } from '@/store';
import { isEmptyObject } from '@/utils/check-helpers';
import { AUSTRALIA, QLD } from '@/constants/general-words.const';
import RadioCardList from '@/components/RadioCardList/RadioCardList.vue';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import RACQFormKitErrorMessage from '@/components/RACQFormKit/RACQFormKitErrorMessage.vue';
import { InformationBoxTypes } from '@/interfaces/forms/information-box.interface';


export default /*@__PURE__*/_defineComponent({
  __name: 'ManualAddressForm',
  props: {
    name: {},
    defaultIsOverseasOption: { type: Boolean },
    showManualEntries: { type: Boolean },
    streetNoRequired: {},
    streetRequired: {},
    suburbRequired: {},
    stateRequired: {},
    postcodeRequired: {},
    countryRequired: {},
    checkAddressRequired: {},
    selectSuburbRequired: {},
    invalidInsuranceRiskSuburbMessageTitle: {},
    invalidInsuranceRiskSuburbMessageText: {},
    noPostcodeMatchesFoundMessage: {},
    riskAddressNotFullyVerifiedMessage: {},
    riskAddressCycloneCheckFailedMessage: {},
    riskSuburbsLabel: {},
    instanceIdentifier: {},
    invalidCharactersRegex: {},
    invalidCharactersMessage: {},
    postcodeInvalid: {},
    throwJpForNonQldAddress: {},
    physicalAddressOnlyPOBoxInvalidMessage: {}
  },
  emits: addressLookupEmits,
  setup(__props: any, { emit: __emit }) {

const props = __props;

const selectedAddress = ref<string>();
const states = ref<IDropdownItem[]>([]);
const countries = ref<IDropdownItem[]>([]);
const suburbs = ref<IRadioButtonCard[]>([]);
const checkAddressRef = ref(null);
const noPostcodeMatches = ref<boolean>();
const streetNumberTextInputValidation = ref<any>();
const triggerStreetNumValidationChange = ref<boolean>(false);

const formStore = useFormStore();
const addressLookupStore = useAddressLookupStore();
const jpStore = useJeopardyStore();

const multiInstanceAddressStore = useMultiInstanceAddressOptionsStore();
const addressLookupOptionsStore = multiInstanceAddressStore.getInstance(props.instanceIdentifier);

const translation = useI18n();

const emit = __emit;

const suburbLookupOptions = computed({
  get() {
    return addressLookupOptionsStore.suburbLookupOptions || [];
  },
  set(newValue) {
    addressLookupOptionsStore.suburbLookupOptions = newValue;
  },
});

const optionalTextInputValidation = computed(() =>
  props.invalidCharactersRegex?.length > 0 ? [['matches', new RegExp(props.invalidCharactersRegex)]] : []
);

const optionalTextInputValidationMessages = computed(() =>
  props.invalidCharactersMessage?.length > 0 ? { matches: props.invalidCharactersMessage } : {}
);

const requiredTextInputValidation = computed(() =>
  props.invalidCharactersRegex?.length > 0
    ? [
        ['required', 'trim'],
        ['matches', new RegExp(props.invalidCharactersRegex)],
      ]
    : [['required', 'trim']]
);

watch(
  () => addressLookupOptionsStore.hasPOBoxEntry(),
  async (newValue, oldValue) => {
    setStreetNumberValidation(newValue);
  }
);

const isValidPOBoxState = computed(() => {
  return addressLookupOptionsStore.hasPOBoxEntry() && !addressLookupOptionsStore.config?.allowPhysicalAddressesOnly;
});

const apiValidationMessage = computed(() => {
  if (addressLookupOptionsStore?.externalValidationMessage) {
    return { title: '', text: addressLookupOptionsStore?.externalValidationMessage };
  } else if (noPostcodeMatches.value) {
    return { title: '', text: props.noPostcodeMatchesFoundMessage };
  } else if (addressLookupOptionsStore.showInvalidInsuranceRiskSuburbMessage) {
    return { title: props.invalidInsuranceRiskSuburbMessageTitle, text: props.invalidInsuranceRiskSuburbMessageText };
  } else if (
    addressLookupOptionsStore.riskAddressNotFullyVerifiedIsError &&
    addressLookupOptionsStore.showRiskAddressNotFullyVerifiedMessage
  ) {
    return { title: '', text: props.riskAddressNotFullyVerifiedMessage };
  } else if (addressLookupOptionsStore.showRiskAddressCycloneCheckFailedMessage) {
    return { title: '', text: props.riskAddressCycloneCheckFailedMessage };
  }
  return null;
});

/**
 * Handles when the manual address is set, here we utilise the suburb lookup options to show selection confirmed.
 */
watch(
  () => addressLookupOptionsStore.manualAddressComponents,
  async (newValue) => {
    if (newValue) {
      const option = getSuburbLookupResult();

      // Postcode is optional for overseas, otherwise mandatory
      if (option.suburb && (addressLookupOptionsStore.isOverseasSelection || option.postcode)) {
        suburbLookupOptions.value = [option];
        selectedAddress.value =
          suburbLookupOptions.value.length > 0 ? JSON.stringify(suburbLookupOptions.value[0]) : '';
      }
    }
  },
  { once: true }
);

watch(
  () => addressLookupOptionsStore.isOverseasSelection,
  async (newValue) => {
    if (newValue && countries.value.length == 0) {
      countries.value = await lookupCountry();
    }

    //Clear validation errors when switching
    reset(`${props.name}_manual`);

    //Prevent clearing of address when initially setting isOverseasSelection.
    if (!addressLookupOptionsStore.componentLoading) {
      addressLookupOptionsStore.resetManualAddress();
    }
  }
);

watch(suburbLookupOptions, (newValue) => {
  suburbs.value = newValue.map((option) => ({
    label: joinAddressComponents({
      ...(addressLookupOptionsStore.manualAddressComponents as any),
      ...option,
    }),
    value: JSON.stringify(option),
  }));
});

onMounted(async () => {
  const dict: Record<string, string> = translation?.getLocaleMessage('en') as any;
  if (dict?.INPUT_ADDRESS_STATE) {
    states.value = dict.INPUT_ADDRESS_STATE.split(',').map((item) => ({ label: item, value: item }));
    addressLookupOptionsStore.manualAddressComponents.state = QLD;
  }

  if (addressLookupOptionsStore.isOverseasSelection) {
    countries.value = await lookupCountry();
  }

  if (addressLookupOptionsStore) {
    addressLookupOptionsStore.componentLoading = false;
  }

  setStreetNumberValidation(addressLookupOptionsStore.hasPOBoxEntry());
});

//Street number needs special attention as it can switch from being required to optional depending
//on whether it's a postal address instance of the component and a PO Box, Care of, Locked Bag etc values are entered.
//Need to be cautious triggering triggerStreetNumValidationChange as it can clear the field but it's also seems to be needed for the validation change to be detected.
function streetNumberCheck() {
  if (addressLookupOptionsStore.manualAddressComponents.streetNumber?.trim() === '') {
    setStreetNumberValidation(addressLookupOptionsStore.hasPOBoxEntry());
    triggerStreetNumValidationChange.value = !triggerStreetNumValidationChange.value;
  }
}

function setStreetNumberValidation(isPOBox: boolean) {
  if (!isPOBox || addressLookupOptionsStore.config?.allowPhysicalAddressesOnly) {
    streetNumberTextInputValidation.value =
      props.invalidCharactersRegex?.length > 0
        ? [
            ['required', 'trim'],
            ['matches', new RegExp(props.invalidCharactersRegex)],
          ]
        : [['required', 'trim']];
    return;
  }
  streetNumberTextInputValidation.value = [['optional']];
}

function handleNonQldAddresses(state?: string): boolean {
  if (props.throwJpForNonQldAddress != null && state != null && state != QLD) {
    jpStore.showJeopardyPanel({
      jpCode: props.throwJpForNonQldAddress,
      isChatEnabled: true,
      isCallUsVisible: true,
    });

    return true;
  }

  return false;
}

function handlePOBoxWhenPhysicalAddressOnly(): boolean {
  if (addressLookupOptionsStore.config?.allowPhysicalAddressesOnly && addressLookupOptionsStore.hasPOBoxEntry()) {
    addressLookupOptionsStore.externalValidationMessage = props.physicalAddressOnlyPOBoxInvalidMessage;
    addressLookupOptionsStore.manualEditEnabled = false;
    return true;
  }

  return false;
}

async function addressSelected(selected: string) {
  if (!selected) return;
  const selectedOption = JSON.parse(selected) as ISuburbLookupResult;
  addressLookupOptionsStore.manualAddressComponents.suburb = selectedOption.suburb;
  addressLookupOptionsStore.manualAddressComponents.state = selectedOption.state;

  // If a non-QLD address is selected and the throwJpForNonQldAddress prop has a value, throw a JP
  // and break out of this function early so no other validation rules/messages are executed
  if (handleNonQldAddresses(addressLookupOptionsStore.manualAddressComponents.state)) return;

  if (handlePOBoxWhenPhysicalAddressOnly()) return;

  await applyRiskAddressBusinessRules();

  suburbLookupOptions.value = suburbLookupOptions.value.filter((item) => item.name === selectedOption.name);

  if (!isEmptyObject(addressLookupOptionsStore.manualAddressComponents)) {
    emit(emitManualAddressSelected, addressLookupOptionsStore.manualAddressComponents);
  }
}

async function lookupCountry(): Promise<IDropdownItem[]> {
  try {
    const data: INameValue[] = await addressLookupStore.getCountryLookup('', true);
    return (
      data?.map((item: INameValue) => ({
        label: item.name,
        value: item.value,
      })) || []
    );
  } catch {
    return [];
  }
}

function getSuburbLookupResult(checkCountry?: boolean): ISuburbLookupResult {
  if (checkCountry && !addressLookupOptionsStore.isOverseasSelection) {
    addressLookupOptionsStore.manualAddressComponents.country = AUSTRALIA;
  }

  return {
    suburb: addressLookupOptionsStore.manualAddressComponents?.suburb,
    postcode: addressLookupOptionsStore.manualAddressComponents?.postcode,
    state: addressLookupOptionsStore.manualAddressComponents?.state,
  } as ISuburbLookupResult;
}

async function checkAddress() {
  // Reset state
  noPostcodeMatches.value = false;
  selectedAddress.value = undefined;
  suburbLookupOptions.value = [];
  addressLookupOptionsStore.clearAllWarningsAndErrors();

  //If in 'Edit address' state enable the form and exit.
  if (!addressLookupOptionsStore.manualEditEnabled) {
    addressLookupOptionsStore.userHasTriggeredChanged = true;
    addressLookupOptionsStore.toggleManualFormDisabled();
    return;
  }

  if (addressLookupOptionsStore.isOverseasSelection) {
    validateOverseasAddressAndEmit();
    return;
  } else {
    addressLookupOptionsStore.manualAddressComponents.country = AUSTRALIA;
    addressLookupOptionsStore.manualAddressComponents.countryISO = '';
  }
  if (handlePOBoxWhenPhysicalAddressOnly()) return;
  streetNumberCheck();

  await addressSuburbLookup();

  addressLookupOptionsStore.manualEditEnabled = false;

  if (suburbLookupOptions.value?.length > 0) {
    return;
  }

  if (isPostalAddress(addressLookupOptionsStore.manualAddressComponents.street ?? '')) {
    if (addressLookupOptionsStore?.config?.allowPhysicalAddressesOnly) {
      addressLookupOptionsStore.manualAddressComponents.street = '';
      if (!isEmptyObject(addressLookupOptionsStore.manualAddressComponents)) {
        emit(emitManualAddressSelected, addressLookupOptionsStore.manualAddressComponents);
      }
    }
  } else {
    addressLookupOptionsStore.manualAddressComponents.addressLine = addressLookupOptionsStore.joinAddressLine(
      addressLookupOptionsStore.manualAddressComponents
    );
    addressLookupOptionsStore.useAddress = addressLookupOptionsStore.manualAddressComponents;

    if (!isEmptyObject(addressLookupOptionsStore.manualAddressComponents)) {
      emit(emitManualAddressSelected, addressLookupOptionsStore.manualAddressComponents);
    }
  }
}

async function addressSuburbLookup() {
  formStore.showLoadingForCurrentStep();

  try {
    const result = await addressLookupOptionsStore.getSuburbsByPostcode();

    if (result?.match) {
      //UX even when there is a single match we want to show the list and prompt to select the address.
      suburbLookupOptions.value = [result?.match];
      //Emit so it gets mapped to the form as it will get auto selected sometimes.
      emit(emitManualAddressSelected, addressLookupOptionsStore.manualAddressComponents);
    } else {
      noPostcodeMatches.value = result?.results?.length == 0;
      suburbLookupOptions.value = result?.results ?? [];
    }
  } catch (e) {
    console.error(e);
  }

  formStore.showLoadingForCurrentStep(false);
}

async function applyRiskAddressBusinessRules() {
  if (addressLookupOptionsStore?.config?.allowValidInsuranceRiskSuburbsOnly) {
    formStore.showLoadingForCurrentStep();

    await addressLookupOptionsStore.applyRiskAddressBusinessRules();

    formStore.showLoadingForCurrentStep(false);
  }
}

function validateOverseasAddressAndEmit() {
  if (addressLookupOptionsStore.manualAddressComponents) {
    if (addressLookupOptionsStore.isOverseasSelection) {
      //Clear the state field as Overseas addresses do not use state.
      addressLookupOptionsStore.manualAddressComponents.state = '';

      const validCountry = countries.value.find(
        (x) => x.value?.toLowerCase() === addressLookupOptionsStore.manualAddressComponents.country?.toLowerCase()
      );

      if (
        validCountry &&
        addressLookupOptionsStore.manualAddressComponents.suburb &&
        addressLookupOptionsStore.manualAddressComponents.suburb.length > 0
      ) {
        //Do this to keep the selected value shown with tick/confirmation as per UX feedback.
        suburbLookupOptions.value = [getSuburbLookupResult()];
        emit(emitManualAddressSelected, addressLookupOptionsStore.manualAddressComponents);
      }
    }
  }
}

return (_ctx: any,_cache: any) => {
  return (_unref(addressLookupOptionsStore).isManualEntries || _unref(addressLookupOptionsStore).isOverseasSelection)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createVNode(_unref(FormKit), {
          id: _ctx.name + '_manual',
          modelValue: _unref(addressLookupOptionsStore).manualAddressComponents,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(addressLookupOptionsStore).manualAddressComponents) = $event)),
          name: _ctx.name + '_manual',
          disabled: !_unref(addressLookupOptionsStore).manualEditEnabled,
          type: "form",
          onSubmit: checkAddress
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_unref(FormKit), {
                "data-testid": "unitNumber",
                name: "unitNumber",
                type: "text",
                maxLength: "100",
                label: "Unit number (optional)",
                disabled: isValidPOBoxState.value || !_unref(addressLookupOptionsStore).manualEditEnabled,
                validation: optionalTextInputValidation.value,
                validationMessages: optionalTextInputValidationMessages.value,
                innerClass: "input-xs",
                autocomplete: "off webauthn"
              }, null, 8, ["disabled", "validation", "validationMessages"]),
              (_openBlock(), _createBlock(_unref(FormKit), {
                key: triggerStreetNumValidationChange.value,
                "data-testid": "streetNumber",
                name: "streetNumber",
                type: "text",
                label: "Street number",
                maxLength: "100",
                messageClass: "md:!pl-[0px]",
                disabled: isValidPOBoxState.value || !_unref(addressLookupOptionsStore).manualEditEnabled,
                validation: streetNumberTextInputValidation.value,
                validationMessages: {
            required: _ctx.streetNoRequired,
            matches: _ctx.invalidCharactersMessage,
          },
                innerClass: "input-xs",
                autocomplete: "off webauthn",
                onBlur: _cache[0] || (_cache[0] = ($event: any) => (streetNumberCheck()))
              }, null, 8, ["disabled", "validation", "validationMessages"])),
              _createVNode(_unref(FormKit), {
                "data-testid": "street",
                name: "street",
                type: "text",
                label: isValidPOBoxState.value ? 'Street (Postal delivery)' : 'Street',
                maxLength: "100",
                validation: requiredTextInputValidation.value,
                validationMessages: { required: _ctx.streetRequired, matches: _ctx.invalidCharactersMessage },
                messageClass: "md:!pl-[0px]",
                outerClass: "col-span-2",
                innerClass: "input-md",
                autocomplete: "off webauthn",
                onBlur: _cache[1] || (_cache[1] = ($event: any) => (streetNumberCheck()))
              }, null, 8, ["label", "validation", "validationMessages"]),
              _createVNode(_unref(FormKit), {
                "data-testid": "suburb",
                name: "suburb",
                type: "text",
                label: "Suburb",
                maxLength: "100",
                validation: requiredTextInputValidation.value,
                messageClass: "md:!pl-[0px]",
                outerClass: "col-span-2",
                validationMessages: { required: _ctx.suburbRequired, matches: _ctx.invalidCharactersMessage },
                innerClass: "input-md",
                autocomplete: "off webauthn"
              }, null, 8, ["validation", "validationMessages"]),
              (!_unref(addressLookupOptionsStore).isOverseasSelection)
                ? (_openBlock(), _createBlock(_unref(FormKit), {
                    key: 0,
                    "data-testid": "state",
                    name: "state",
                    type: "dropdown",
                    options: states.value,
                    label: "State",
                    validation: "required",
                    messagesClass: "md:ml-[0px] md:pl-[0px]",
                    validationMessages: { required: _ctx.stateRequired },
                    innerClass: "input-xs"
                  }, null, 8, ["options", "validationMessages"]))
                : _createCommentVNode("", true),
              (!_unref(addressLookupOptionsStore).isOverseasSelection)
                ? (_openBlock(), _createBlock(_unref(FormKit), {
                    key: 1,
                    "data-testid": "postcode",
                    name: "postcode",
                    type: "tel",
                    maxLength: "4",
                    label: "Postcode",
                    messageClass: "md:!pl-[0px]",
                    validation: [
            ['required', 'trim'],
            ['matches', /\d{4,4}/],
          ],
                    validationMessages: { required: _ctx.postcodeRequired, matches: _ctx.postcodeInvalid },
                    innerClass: "input-xs",
                    autocomplete: "off webauthn"
                  }, null, 8, ["validationMessages"]))
                : (_openBlock(), _createBlock(_unref(FormKit), {
                    key: 2,
                    name: "postcode",
                    type: "text",
                    "data-testid": "postcode",
                    label: "Postcode (Optional)",
                    validation: optionalTextInputValidation.value,
                    validationMessages: optionalTextInputValidationMessages.value,
                    maxLength: "6",
                    outerClass: "col-span-2",
                    innerClass: "input-xs",
                    autocomplete: "off webauthn"
                  }, null, 8, ["validation", "validationMessages"])),
              (_unref(addressLookupOptionsStore).isOverseasSelection)
                ? (_openBlock(), _createBlock(_unref(FormKit), {
                    key: 3,
                    "data-testid": "country",
                    name: "country",
                    type: "autocomplete",
                    maxLength: "100",
                    label: "Country",
                    placeholder: "Please select",
                    validation: "required",
                    validationMessages: { required: _ctx.countryRequired },
                    messageClass: "md:!pl-[0px]",
                    outerClass: "col-span-2",
                    innerClass: "input-md",
                    options: countries.value,
                    openOnFocus: "",
                    openOnClick: "",
                    clearSearchOnOpen: ""
                  }, null, 8, ["validationMessages", "options"]))
                : _createCommentVNode("", true),
              (_unref(addressLookupOptionsStore).manualEditEnabled)
                ? (_openBlock(), _createBlock(_unref(FormKit), {
                    key: 4,
                    type: "submit",
                    inputClass: "btn btn-md btn-primary mt-3",
                    label: "Submit address",
                    disabled: false
                  }))
                : (_openBlock(), _createBlock(_unref(FormKit), {
                    key: 5,
                    type: "button",
                    inputClass: "btn btn-md btn-primary mt-3",
                    label: "Edit address",
                    disabled: false,
                    onClick: checkAddress
                  }))
            ])
          ]),
          _: 1
        }, 8, ["id", "modelValue", "name", "disabled"]),
        (_unref(addressLookupOptionsStore).manualEditEnabled)
          ? (_openBlock(), _createBlock(_unref(FormKit), {
              key: 0,
              ref_key: "checkAddressRef",
              ref: checkAddressRef,
              outerClass: "hidden",
              validation: "required",
              validationMessages: { required: _ctx.checkAddressRequired }
            }, null, 8, ["validationMessages"]))
          : _createCommentVNode("", true),
        _createVNode(RACQFormKitErrorMessage, {
          node: (checkAddressRef.value as any)?.node,
          description: _ctx.checkAddressRequired
        }, null, 8, ["node", "description"]),
        (
        (_unref(addressLookupOptionsStore).userHasTriggeredChanged ||
          !_unref(addressLookupOptionsStore).config?.manualAddressConfirmationOnChangeOnly) &&
        suburbLookupOptions.value?.length
      )
          ? (_openBlock(), _createBlock(RadioCardList, {
              key: 1,
              modelValue: selectedAddress.value,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((selectedAddress).value = $event)),
              bindings: {
        name: 'suburbLookup',
        label: _ctx.riskSuburbsLabel,
        options: suburbs.value,
        optionsClass: 'racq-max-w-xl',
        validation: [['required', 'trim']],
        validationMessages: { required: _ctx.selectSuburbRequired },
        onInput: addressSelected,
      }
            }, null, 8, ["modelValue", "bindings"]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_3, [
          (apiValidationMessage.value)
            ? (_openBlock(), _createBlock(InformationBox, {
                key: 0,
                title: apiValidationMessage.value.title,
                description: apiValidationMessage.value.text,
                type: _unref(InformationBoxTypes).ERROR
              }, null, 8, ["title", "description", "type"]))
            : _createCommentVNode("", true),
          (apiValidationMessage.value)
            ? (_openBlock(), _createBlock(_unref(FormKit), {
                key: 1,
                outerClass: "hidden",
                validation: "required",
                validationMessages: { required: apiValidationMessage.value },
                validationVisibility: "live"
              }, null, 8, ["validationMessages"]))
            : _createCommentVNode("", true),
          (
          !apiValidationMessage.value &&
          _unref(addressLookupOptionsStore) &&
          !_unref(addressLookupOptionsStore).riskAddressNotFullyVerifiedIsError &&
          _unref(addressLookupOptionsStore).showRiskAddressNotFullyVerifiedMessage
        )
            ? (_openBlock(), _createBlock(InformationBox, {
                key: 2,
                description: _ctx.riskAddressNotFullyVerifiedMessage,
                type: 
          _unref(addressLookupOptionsStore).riskAddressNotFullyVerifiedIsError
            ? _unref(InformationBoxTypes).ERROR
            : _unref(InformationBoxTypes).WARNING
        
              }, null, 8, ["description", "type"]))
            : _createCommentVNode("", true),
          (_unref(addressLookupOptionsStore)?.showDividerForManualAddress)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4))
            : _createCommentVNode("", true)
        ])
      ]))
    : _createCommentVNode("", true)
}
}

})